import type { DisconnectionLinks, EarthFaultLinks } from "../services/calculationRun";
import { formatQuantity } from "../utils/formatQuantity";
import { CASE_LABELS, FAULT_TYPE_LABELS } from "./starterCoordinationDraft";
import { StatusIcon } from "./StatusIcon";

// The stored runs the conductor rows of an earthing study read their figures from, pinned per
// row in the run's references snapshot (EOS-08a, A36): `fault_links` keyed by the row code (a
// board conductor's earth-fault current from an EOS-04 run, D3) and `disconnection_links`
// keyed by the row code (a circuit protective conductor's t, I and S from a row of an EOS-05d
// run, D5) - the run and its revision, the figures as read with the exact decimals in the
// tooltips (A12), the run's own status, and a REVIEW outcome with one card per warning. Nothing
// is shown for a kind no row named (that panel is not rendered at all).

export const EARTH_FAULT_LINK_CODE_LABELS: Record<string, string> = {
  FAULT_RUN_NOT_MAXIMUM_EARTH_FAULT: "Not the maximum single-phase-to-earth fault",
  FAULT_RUN_BUS_MISMATCH: "Fault run of another bus",
};

export const DISCONNECTION_LINK_CODE_LABELS: Record<string, string> = {
  DISCONNECTION_RUN_BUS_MISMATCH: "Earth-fault loop run of another bus",
  DISCONNECTION_ROW_NOT_ASSURED: "Linked row's disconnection not assured",
  DISCONNECTION_ROW_NOT_COMPARED: "Linked row's disconnection not compared",
};

/** "t 0.4 s · I_f 477.0 A · S 2.5 mm² copper · disconnection assured · closes the thermal check" */
export function disconnectionLinkDetailText(link: DisconnectionLinks[string]): string {
  const time = link.required_disconnection_time_s === null ? "t —" : `t ${formatQuantity(link.required_disconnection_time_s, "s").display}`;
  const current = link.fault_current_a === null ? "I_f —" : `I_f ${formatQuantity(link.fault_current_a, "A").display}`;
  const material = link.conductor_material === null ? "" : ` ${link.conductor_material.toLowerCase()}`;
  const area =
    link.protective_conductor_area_mm2 === null
      ? "S —"
      : `S ${formatQuantity(link.protective_conductor_area_mm2, "mm²").display}${material}`;
  const assured =
    link.disconnection_assured === null
      ? "disconnection not compared"
      : link.disconnection_assured
        ? "disconnection assured"
        : "disconnection not assured";
  const closes = link.closes_thermal_not_checked ? " · closes the thermal check" : "";
  return `${time} · ${current} · ${area} · ${assured}${closes}`;
}

function linkWord(count: number, singular: string, plural: string): string {
  return `${count} linked ${count === 1 ? singular : plural}`;
}

function outcome(status: string, warnings: number): string {
  if (status !== "VALID") {
    return `Review (${warnings})`;
  }
  return warnings === 0 ? "Valid" : `Valid (${warnings} ${warnings === 1 ? "note" : "notes"})`;
}

type EarthingLinksPanelProps = {
  faultLinks: EarthFaultLinks | null | undefined;
  disconnectionLinks: DisconnectionLinks | null | undefined;
};

export function EarthingLinksPanel({ faultLinks, disconnectionLinks }: EarthingLinksPanelProps) {
  const faults = faultLinks ? Object.entries(faultLinks) : [];
  const rows = disconnectionLinks ? Object.entries(disconnectionLinks) : [];
  return (
    <>
      {faults.length > 0 ? (
        <section
          aria-label="Earth-fault runs linked"
          data-summary-tone={faults.every(([, link]) => link.status === "VALID") ? "pass" : "warning"}
        >
          <header>
            <p data-summary-status={faults.every(([, link]) => link.status === "VALID") ? "VALID" : "REVIEW_REQUIRED"}>
              <StatusIcon tone={faults.every(([, link]) => link.status === "VALID") ? "pass" : "review"} size={20} />
              {`Earth-fault current from ${linkWord(faults.length, "fault run", "fault runs")}: ${
                faults.every(([, link]) => link.status === "VALID") ? "valid" : "review required"
              }`}
            </p>
          </header>
          <div data-table-scroll>
            <table>
              <thead>
                <tr>
                  <th scope="col">Conductor</th>
                  <th scope="col">Fault run</th>
                  <th scope="col">Earth-fault current as read</th>
                  <th scope="col">Case</th>
                  <th scope="col">Link</th>
                </tr>
              </thead>
              <tbody>
                {faults.map(([code, link]) => (
                  <tr key={code} data-earth-fault-link={code} data-earth-fault-link-status={link.status}>
                    <th scope="row">{code}</th>
                    <td title={link.fault_run_id}>
                      {link.calculation_key} rev {link.revision_number}
                    </td>
                    <td
                      data-earth-fault-link-current
                      title={link.earth_fault_current_ka === null ? undefined : formatQuantity(link.earth_fault_current_ka, "kA").exact ?? undefined}
                    >
                      {link.earth_fault_current_ka === null
                        ? "not stored"
                        : `${formatQuantity(link.earth_fault_current_ka, "kA").display} · bus ${link.fault_bus_code}`}
                    </td>
                    <td>
                      {(CASE_LABELS as Record<string, string>)[link.calculation_case] ?? link.calculation_case} ·{" "}
                      {(FAULT_TYPE_LABELS as Record<string, string>)[link.fault_type] ?? link.fault_type}
                    </td>
                    <td>{outcome(link.status, link.warnings.length)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {faults.some(([, link]) => link.warnings.length > 0) ? (
            <ul data-warning-count={faults.reduce((count, [, link]) => count + link.warnings.length, 0)}>
              {faults.flatMap(([code, link]) =>
                link.warnings.map((warning, index) => (
                  <li key={`${code}-${warning.code}-${index}`} data-warning-code={warning.code} data-warning-tone="review">
                    <strong>
                      <StatusIcon tone="review" size={16} />
                      Conductor {code}: {EARTH_FAULT_LINK_CODE_LABELS[warning.code] ?? warning.code}
                    </strong>
                    {": "}
                    <span>{warning.message}</span>
                  </li>
                )),
              )}
            </ul>
          ) : null}
        </section>
      ) : null}
      {rows.length > 0 ? (
        <section
          aria-label="Earth-fault loop rows linked"
          data-summary-tone={rows.every(([, link]) => link.status === "VALID") ? "pass" : "warning"}
        >
          <header>
            <p data-summary-status={rows.every(([, link]) => link.status === "VALID") ? "VALID" : "REVIEW_REQUIRED"}>
              <StatusIcon tone={rows.every(([, link]) => link.status === "VALID") ? "pass" : "review"} size={20} />
              {`t, I and S from ${linkWord(rows.length, "earth-fault loop row", "earth-fault loop rows")}: ${
                rows.every(([, link]) => link.status === "VALID") ? "valid" : "review required"
              }`}
            </p>
          </header>
          <div data-table-scroll>
            <table>
              <thead>
                <tr>
                  <th scope="col">Conductor</th>
                  <th scope="col">Earth-fault loop run · row</th>
                  <th scope="col">Figures as read</th>
                  <th scope="col">Row status</th>
                  <th scope="col">Link</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(([code, link]) => (
                  <tr key={code} data-disconnection-link={code} data-disconnection-link-status={link.status}>
                    <th scope="row">{code}</th>
                    <td title={link.content_hash}>
                      {link.calculation_key} rev {link.revision_number} · row {link.row_code}
                    </td>
                    <td
                      title={`${link.required_disconnection_time_s ?? "—"} s · ${link.fault_current_a ?? "—"} A · ${link.protective_conductor_area_mm2 ?? "—"} mm²`}
                    >
                      {disconnectionLinkDetailText(link)}
                    </td>
                    <td data-disconnection-row-status={link.row_status}>{link.row_status.replaceAll("_", " ").toLowerCase()}</td>
                    <td>{outcome(link.status, link.warnings.length)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {rows.some(([, link]) => link.warnings.length > 0) ? (
            <ul data-warning-count={rows.reduce((count, [, link]) => count + link.warnings.length, 0)}>
              {rows.flatMap(([code, link]) =>
                link.warnings.map((warning, index) => (
                  <li key={`${code}-${warning.code}-${index}`} data-warning-code={warning.code} data-warning-tone="review">
                    <strong>
                      <StatusIcon tone="review" size={16} />
                      Conductor {code}: {DISCONNECTION_LINK_CODE_LABELS[warning.code] ?? warning.code}
                    </strong>
                    {": "}
                    <span>{warning.message}</span>
                  </li>
                )),
              )}
            </ul>
          ) : null}
        </section>
      ) : null}
    </>
  );
}

export default EarthingLinksPanel;
